/**
 * orientation.js — pure utility shared by 2D Elevation Views and 3D scene.
 * Lives outside `/components/r3d/` so importing it doesn't pull in
 * @react-three/fiber + drei into 2D component trees.
 *
 * v1.64 — switched from `{rotation_deg + orientation_flip}` (4 combos) to a
 * full `axis_map` permutation (all 6 cube orientations reachable) so the
 * user can compose Flip-Vertical + Flip-Horizontal sequentially from the
 * CURRENT pose. Legacy items without axis_map are derived from the old
 * flags at read-time for backward compat.
 */

const DIM_KEY    = { length: "length_in", width: "width_in", depth: "depth_in" };
const DIM_FALLBK = { length: 24,          width: 18,          depth: 60         };

/** Default axis_map matches the user's mental model:
 *    worldX ← width_in  (horizontal extent along the wall)
 *    worldY ← length_in (vertical extent — the "height" of a typical panel)
 *    worldZ ← depth_in  (wall-perpendicular thickness)
 *  v1.68 — changed from `['length','depth','width']` (which rendered panels
 *  lying on their side) to `['width','length','depth']` after user feedback
 *  that EVERY 2D/3D view looked wrong because length was being treated as
 *  the wall-horizontal dimension instead of the height.
 */
export const DEFAULT_AXIS_MAP = ["width", "length", "depth"];

/** Resolve an item's world-frame oriented bounding box (inches).
 *    wallSize  — horizontal dimension parallel to the wall (worldX)
 *    vertSize  — vertical dimension (worldY)
 *    thickness — wall-perpendicular dimension (worldZ)
 *
 *  Source of truth (v1.64):
 *    1. `item.axis_map` ([worldX_key, worldY_key, worldZ_key]) if present.
 *    2. Otherwise derived from legacy `rotation_deg` + `orientation_flip`.
 */
export function orientedDimensions(item) {
  const axisMap = resolveAxisMap(item);
  const get = (k) => Number(item[DIM_KEY[k]]) || DIM_FALLBK[k];
  return {
    wallSize:  get(axisMap[0]),
    vertSize:  get(axisMap[1]),
    thickness: get(axisMap[2]),
    axis_map:  axisMap,
    // Legacy compatibility fields — kept for any code that reads them.
    isYawed:   axisMap[0] !== DEFAULT_AXIS_MAP[0],
    isFlipped: axisMap[1] !== DEFAULT_AXIS_MAP[1],
    yawDeg:    Number(item.rotation_deg) || 0,
  };
}

/** Internal — convert any item to its axis_map, deriving from legacy
 *  flags if necessary. v1.68: when neither axis_map nor legacy flags are
 *  set, returns the new DEFAULT_AXIS_MAP (`['width','length','depth']`)
 *  so panels stand upright by default. When legacy flags ARE set (a real
 *  user choice from pre-v1.64), we honour them via the legacy derivation. */
function resolveAxisMap(item) {
  const am = item.axis_map;
  if (Array.isArray(am) && am.length === 3 && am.every((k) => k in DIM_KEY)) {
    return am;
  }
  // If the item has NO legacy flags either, use the new upright default.
  const hasLegacyFlags = ("orientation_flip" in item && item.orientation_flip !== null && item.orientation_flip !== undefined)
                       || ("rotation_deg"     in item && item.rotation_deg     !== null && item.rotation_deg     !== undefined);
  if (!hasLegacyFlags) return DEFAULT_AXIS_MAP.slice();
  // Legacy derivation (pre-v1.64) — preserved so explicit pre-v1.64 user
  // choices keep their original rendering.
  const isFlipped = !!item.orientation_flip;
  const yawDeg    = ((Number(item.rotation_deg) || 0) % 360 + 360) % 360;
  const isYawed   = (yawDeg === 90 || yawDeg === 270);
  const baseHoriz = "length";
  const baseVert  = isFlipped ? "width" : "depth";
  const thickness = isFlipped ? "depth" : "width";
  return [
    isYawed ? baseVert  : baseHoriz,
    isYawed ? baseHoriz : baseVert,
    thickness,
  ];
}

/** Flip Vertical — rotate the box 90° about its current world-X axis.
 *  Effect on the AABB: world-Y ↔ world-Z swap. Applied to the current
 *  pose (NOT the legacy flags). Returns a NEW axis_map; caller is
 *  responsible for setting it on the item + clamping position. */
export function flipVerticalAxisMap(item) {
  const am = resolveAxisMap(item);
  return [am[0], am[2], am[1]];
}

/** Flip Horizontal — rotate the box 90° about its current world-Y axis.
 *  Effect on the AABB: world-X ↔ world-Z swap. */
export function flipHorizontalAxisMap(item) {
  const am = resolveAxisMap(item);
  return [am[2], am[1], am[0]];
}
